import { useEffect, useRef, useState } from "react";

/**
 * Renders a Bunny Stream embed inside the dark thumb frame.
 *
 * Layering (back → front):
 *   z-0  iframe   (mounts lazily or eagerly, autoplays muted)
 *   z-1  poster   (Bunny thumbnail — what users see instantly)
 *   z-2  loader   (shimmer + play icon, on top of poster)
 *
 * Reveal logic:
 *   `iframe.onLoad` only fires when the embed HTML loads — NOT when video
 *   frames are actually rendering. Bunny HLS needs ~2–4s to start streaming.
 *   To avoid flashing Bunny's dark loading shell, we:
 *     1) Listen for the Player.js `play` postMessage event from the iframe.
 *        That's the real signal frames are being painted — fade poster instantly.
 *     2) Fall back to a generous timeout (3500ms after onLoad) so we never
 *        get stuck showing the poster forever if postMessage is blocked.
 */
export default function VideoFrame({
  src,
  poster,
  title,
  aspect = "aspect-video",
  eager = false,
  className = "",
}) {
  const wrapRef = useRef(null);
  const iframeRef = useRef(null);
  const [inView, setInView] = useState(eager);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);

  // Lazy-mount the iframe ~500px before the card enters the viewport.
  useEffect(() => {
    if (eager) return;
    const el = wrapRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { rootMargin: "500px 0px", threshold: 0 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [eager]);

  // Listen for Bunny Player.js `play` events — the real signal that video
  // frames are now painting. When we get it, fade the poster immediately.
  useEffect(() => {
    if (!inView) return;
    const onMessage = (event) => {
      const origin = event.origin || "";
      if (!origin.includes("mediadelivery.net") && !origin.includes("b-cdn.net")) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      let data = event.data;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch { return; }
      }
      // Player.js dispatches { event: 'play' } when playback begins.
      // Also accept 'playing' / 'timeupdate' as proof that frames are flowing.
      const evt = data?.event || data?.type;
      if (evt === "play" || evt === "playing" || evt === "timeupdate") {
        setVideoPlaying(true);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [inView]);

  // Fallback: if Player.js events never fire, reveal the video 3.5s after
  // the iframe DOM has loaded — long enough for HLS to buffer in most cases.
  useEffect(() => {
    if (!iframeLoaded) return;
    const t = setTimeout(() => setVideoPlaying(true), 3500);
    return () => clearTimeout(t);
  }, [iframeLoaded]);

  const url = `${src}?autoplay=true&loop=true&muted=true&preload=true`;

  return (
    <div
      ref={wrapRef}
      className={`thumb-placeholder ${aspect} overflow-hidden ${className}`}
      data-testid="video-frame"
      aria-busy={!videoPlaying}
    >
      {/* z-0: iframe — mounts lazily or eagerly, plays in background */}
      {inView && (
        <iframe
          ref={iframeRef}
          src={url}
          title={title}
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowFullScreen
          onLoad={() => setIframeLoaded(true)}
          className="absolute inset-0 w-full h-full border-0"
          style={{ zIndex: 0 }}
        />
      )}

      {/* z-1: poster — visible until video is confirmed playing */}
      {poster && (
        <img
          src={poster}
          alt=""
          aria-hidden="true"
          loading="eager"
          decoding="async"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            videoPlaying ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
          style={{ zIndex: 1 }}
          data-testid="video-poster"
        />
      )}

      {/* z-2: loader sits over the poster as a subtle "demo loading" cue
          and stays visible until the video is actually playing */}
      <div
        className={`video-loader ${videoPlaying ? "is-loaded" : ""} ${poster ? "video-loader--over-poster" : ""}`}
        aria-hidden="true"
      >
        <div className="video-loader-shimmer" />
        <div className="video-loader-center">
          <div className="video-loader-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M5 3l16 9-16 9V3z" fill="#00B4A6" opacity="0.95" />
            </svg>
          </div>
          <div className="video-loader-caption">Loading demo…</div>
        </div>
      </div>
    </div>
  );
}

/**
 * LazyVideoFrame — defers iframe loading until the component
 * approaches the viewport (IntersectionObserver with 200px rootMargin).
 *
 * While the iframe hasn't been activated:
 *  - A shimmer skeleton fills the thumb-placeholder area
 *  - The play-button overlay remains visible on top
 *
 * Once loaded content starts rendering, the iframe fades in smoothly.
 */
export function LazyVideoFrame({ src, title, aspect = "aspect-video", className = "" }) {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const handleIframeLoad = useCallback(() => {
    setIframeLoaded(true);
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const url = `${src}?autoplay=true&loop=true&muted=true&preload=true`;

  return (
    <div
      ref={containerRef}
      className={`thumb-placeholder ${aspect} overflow-hidden ${className}`}
      data-testid="video-frame-lazy"
    >
      {/* Play button overlay — always visible */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
        <div className="h-12 w-12 rounded-full border border-teal/50 flex items-center justify-center">
          <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 3l16 9-16 9V3z" fill="#00B4A6" opacity="0.85" />
          </svg>
        </div>
      </div>

      {/* Shimmer skeleton — visible until iframe content is ready */}
      {!iframeLoaded && (
        <div className="absolute inset-0 z-[5]" aria-hidden="true">
          <div className="video-shimmer w-full h-full" />
        </div>
      )}

      {/* Loading spinner — subtle teal pulse in the center behind play button */}
      {!iframeLoaded && (
        <div className="absolute inset-0 flex items-center justify-center z-[6] pointer-events-none" aria-hidden="true">
          <div className="video-spinner" />
        </div>
      )}

      {/* Iframe — only receives src when near viewport, fades in on load */}
      {isVisible && (
        <iframe
          src={url}
          title={title}
          onLoad={handleIframeLoad}
          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
          allowFullScreen
          className={`absolute inset-0 w-full h-full border-0 transition-opacity duration-500 ${
            iframeLoaded ? "opacity-100" : "opacity-0"
          }`}
        />
      )}
    </div>
  );
}